import { useState, useEffect, useCallback } from "react";
import { api } from "../api";

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return "—";
  const [year, month, day] = d.split("T")[0].split("-");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── Payment History ──────────────────────────────────────────────────────────
export function PaymentsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    api.getPayments(params.toString())
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Payment History</div>
        <div className="page-subtitle">All payments recorded on your account.</div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        {loading
          ? <div className="loading">Loading payments…</div>
          : data?.payments?.length === 0
            ? (
              <div className="empty-state">
                <div className="empty-state-icon">◈</div>
                <p>No payment history found.</p>
              </div>
            )
            : (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Reference #</th>
                        <th className="td-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.map(pmt => (
                        <tr key={pmt.id}>
                          <td>{fmtDate(pmt.payment_date)}</td>
                          <td>{pmt.payment_method || "—"}</td>
                          <td style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
                            {pmt.reference_number || "—"}
                          </td>

                          <td className="td-right td-mono" style={{ fontWeight: 600, color: "var(--paid)" }}>
                            {fmt(pmt.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.pagination && data.pagination.pages > 1 && (
                  <div className="pagination">
                    <span>Page {data.pagination.page} of {data.pagination.pages}</span>
                    <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                    <button className="btn btn-outline btn-sm" disabled={page >= data.pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
                  </div>
                )}
              </>
            )
        }
      </div>
    </div>
  );
}

// ─── Account ──────────────────────────────────────────────────────────────────
export function AccountPage() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.getAccount()
      .then(setAccount)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading account…</div>;
  if (error)   return <div className="alert alert-error">{error}</div>;

  const addr = account?.billing_address || {};

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Account</div>
        <div className="page-subtitle">Your account information as it appears in our records.</div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Company Information</span></div>
        <div className="card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 28 }}>
            {[
              ["Customer Name", account?.customer_name || account?.company_name],
              ["Contact Name", [account?.first_name, account?.last_name].filter(Boolean).join(" ") || "—"],
              ["Email", account?.email || "—"],
              ["Phone", account?.phone || "—"],
              ["Billing Address", [addr.line1, addr.line2, addr.city && `${addr.city}, ${addr.state} ${addr.zip}`].filter(Boolean).join("\n") || "—"],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 }}>
                  {label}
                </div>
                <div style={{ fontWeight: 400, whiteSpace: "pre-line" }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="alert alert-info" style={{ marginTop: 20 }}>
        To update your account information, please contact us directly. Changes are managed through our accounting system.
      </div>
    </div>
  );
}
